import { z } from 'zod';

export const patientSchema = z.object({
  id: z.number(),
  user: z
    .object({
      id: z.number(),
      first_name: z.string().nullish(),
      last_name: z.string().nullish(),
      email: z.string().nullish(),
    })
    .passthrough()
    .nullish(),
  personal_pronouns: z.string().nullish(),
  date_birth: z.string().nullish(),
  is_patient: z.boolean().nullish(),
  is_provider: z.boolean().nullish(),
  patient_referral: z.string().nullish(),
  referral_details: z.string().nullish(),
  opt_note: z.string().nullish(),
  promo_code: z.string().nullish(),
  onboarding_step: z.string().nullish(),
  completed_onboarding: z.boolean().nullish(),
  phone: z.string().nullish(),
  address_line_1: z.string().nullish(),
  address_line_2: z.string().nullish(),
  address_city: z.string().nullish(),
  address_state: z.string().nullish(),
  address_zip: z.string().nullish(),
  primary_care_doctor_name: z.string().nullish(),
  primary_care_doctor_medical_practice: z.string().nullish(),
  primary_care_doctor_phone: z.string().nullish(),
  primary_care_doctor_fax: z.string().nullish(),
  emergency_contact_name: z.string().nullish(),
  emergency_contact_phone: z.string().nullish(),
  emergency_contact_relation: z.string().nullish(),
  occupation: z.string().nullish(),
  movement_amount: z.string().nullish(),
  goals: z.string().nullish(),
  outlook: z.string().nullish(),
  sleep: z.string().nullish(),
  exercise: z.string().nullish(),
  ability: z.string().nullish(),
  stress: z.string().nullish(),
  pain: z.string().nullish(),
  diet: z.string().nullish(),
  family_history: z.string().nullish(),
  surgeries: z.string().nullish(),
  medications: z.string().nullish(),
  has_guardian: z.boolean().nullish(),
  guardian_first_name: z.string().nullish(),
  guardian_last_name: z.string().nullish(),
  guardian_phone: z.string().nullish(),
  guardian_email: z.string().nullish(),
  guardian_relation: z.string().nullish(),
});

export const appointmentSchema = z
  .object({
    id: z.number(),
    date_created: z.string().nullish(),
    date_updated: z.string().nullish(),
    appointment_scheduled: z
      .object({
        id: z.number(),
        patient: patientSchema.nullish(),
        session_id: z.number().nullish(),
        session_type_id: z.number().nullish(),
        session_type: z
          .object({
            id: z.number(),
            title: z.string().nullish(),
            duration_minutes: z.number().nullish(),
            hourly_rate: z.number().nullish(),
            is_physical_therapy: z.boolean().nullish(),
            hide_availability: z.boolean().nullish(),
            send_calendar_invitations: z.boolean().nullish(),
            css_color: z.string().nullish(),
            location_type: z.string().nullish(),
            description: z.string().nullish(),
            session_meeting_url: z.string().nullish(),
            timezone: z.string().nullish(),
            date_range_start: z.string().nullish(),
            date_range_end: z.string().nullish(),
            scheduling_window_hours: z.number().nullish(),
            time_buffer_before_minutes: z.number().nullish(),
            time_buffer_after_minutes: z.number().nullish(),
            maximum_bookings_per_day: z.number().nullish(),
            address_line_1: z.string().nullish(),
            address_line_2: z.string().nullish(),
            address_city: z.string().nullish(),
            address_state: z.string().nullish(),
            address_zip: z.string().nullish(),
          })
          .passthrough()
          .nullish(),
        start_time: z.string().nullish(),
        end_time: z.string().nullish(),
        timezone: z.string().nullish(),
        start_time_with_buffer: z.string().nullish(),
        end_time_with_buffer: z.string().nullish(),
        time_buffer_before_minutes: z.number().nullish(),
        time_buffer_after_minutes: z.number().nullish(),
        address_line_1: z.string().nullish(),
        address_line_2: z.string().nullish(),
        address_city: z.string().nullish(),
        address_state: z.string().nullish(),
        address_zip: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    category: z.string().nullish(),
    date: z.string().nullish(),
    duration: z.number().nullish(),
    location: z.string().nullish(),
    discount: z.number().nullish(),
    state: z.string().nullish(),
    place_service: z.string().nullish(),
    total: z.number().nullish(),
    rate: z.number().nullish(),
    is_confirmed: z.boolean().nullish(),
    is_canceled: z.boolean().nullish(),
    is_no_show: z.boolean().nullish(),
    is_completed: z.boolean().nullish(),
    is_upcoming: z.boolean().nullish(),
    date_canceled: z.string().nullish(),
    date_confirmed: z.string().nullish(),
    date_completed: z.string().nullish(),
    notes: z.string().nullish(),
    cancel_note: z.string().nullish(),
    invoice_url: z.string().nullish(),
    note_url: z.string().nullish(),
  })
  .passthrough();

const chartSchema = z.object({
  id: z.number(),
  session_id: z.number(),
  session: z.object({
    id: z.number(),
    date_created: z.string(),
    date_updated: z.string(),
    patient: z.object({
      id: z.number(),
      user_id: z.number(),
      user: z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
        email: z.string(),
        is_superuser: z.boolean(),
      }),
      personal_pronouns: z.string(),
      selected_provider: z.null(),
      date_birth: z.string(),
      is_patient: z.boolean(),
      is_provider: z.boolean(),
      patient_referral: z.string(),
      referral_details: z.string(),
      opt_note: z.string(),
      promo_code: z.null(),
      onboarding_step: z.string(),
      completed_onboarding: z.boolean(),
      platform_version: z.string(),
      phone: z.string(),
      address_line_1: z.string(),
      address_line_2: z.string(),
      address_city: z.string(),
      address_state: z.string(),
      address_zip: z.string(),
      primary_care_doctor_name: z.string(),
      primary_care_doctor_medical_practice: z.string(),
      primary_care_doctor_phone: z.string(),
      primary_care_doctor_fax: z.string(),
      emergency_contact_name: z.string(),
      emergency_contact_phone: z.string(),
      emergency_contact_relation: z.string(),
      occupation: z.string(),
      movement_amount: z.string(),
      goals: z.string(),
      outlook: z.string(),
      sleep: z.string(),
      exercise: z.string(),
      ability: z.string(),
      stress: z.string(),
      pain: z.string(),
      diet: z.string(),
      family_history: z.string(),
      surgeries: z.string(),
      medications: z.string(),
      has_guardian: z.boolean(),
      guardian_first_name: z.string(),
      guardian_last_name: z.string(),
      guardian_phone: z.string(),
      guardian_email: z.string(),
      guardian_relation: z.string(),
    }),
    provider: z.object({
      id: z.number(),
      user_id: z.number(),
      user: z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
      }),
      highlights: z.array(z.unknown()),
      is_provider: z.boolean(),
      is_patient: z.boolean(),
      weekly_session_goal: z.number(),
      is_beta_tester: z.boolean(),
      first_name: z.string(),
      last_name: z.string(),
      phone: z.null(),
      address_city: z.string(),
      address_state: z.string(),
      address_zip: z.string(),
      is_verified: z.boolean(),
      slug: z.string(),
      honorific: z.string(),
      title: z.string(),
      gender: z.null(),
      personal_pronouns: z.null(),
      bio: z.null(),
      primary_location_city: z.null(),
      primary_location_state: z.null(),
      education: z.null(),
      credentials: z.string(),
      rate: z.number(),
      timezone: z.string(),
      linkedin_url: z.string(),
      instagram_url: z.string(),
      location_strings: z.null(),
      who_i_treat: z.null(),
      experience_category: z.null(),
      location_categories: z.null(),
      specialty_categories: z.null(),
      treatment_categories: z.null(),
      public_profile_img_url: z.string(),
      public_map_img_url: z.string(),
      radius: z.null(),
      lat: z.null(),
      lon: z.null(),
    }),
    appointment_scheduled: z.null(),
    category: z.string(),
    date: z.string(),
    duration: z.number(),
    location: z.string(),
    discount: z.number(),
    state: z.string(),
    place_service: z.string(),
    total: z.null(),
    rate: z.number(),
    is_confirmed: z.boolean(),
    is_canceled: z.boolean(),
    is_no_show: z.boolean(),
    is_completed: z.boolean(),
    is_upcoming: z.boolean(),
    date_canceled: z.string(),
    date_confirmed: z.string(),
    date_completed: z.null(),
    notes: z.null(),
    cancel_note: z.null(),
    pdfs_saved: z.boolean(),
    platform_version: z.string(),
  }),
  date_created: z.string(),
  date_updated: z.string(),
  poc_date: z.null(),
  comment_objective: z.string(),
  comment_treatment: z.string(),
  comment_subjective: z.string(),
  comment_assessment: z.string(),
  comment_interventions: z.string(),
  addendum_reason: z.string(),
  poc_additional: z.string(),
  poc_goals: z.string(),
  poc_duration: z.string(),
  summary: z.string(),
  code_diagnosis: z.string(),
  cpt_codes: z.array(
    z.object({
      id: z.number(),
      code: z.string(),
      modifier: z.null(),
      units: z.number(),
    }),
  ),
});

export type Appointment = z.infer<typeof appointmentSchema>;
export type AppointmentsResponse = Appointment[];
export type Patient = z.infer<typeof patientSchema>;
export type Chart = z.infer<typeof chartSchema>;
